<script lang="ts">

    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {useForm} from "@/lib/precognition.svelte";
    import {toast} from "svelte-sonner";
    import type Vehicles from "@/schemas/public/Vehicles";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        vehicle : Vehicles;
    };

    let {blocks, show = $bindable(), vehicle }: Props = $props();
    let jobLabel: string | null | undefined = $state(null);


    let formInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;


            if (field.ui_type === 'related') {
                formInner[field.column_name + "_entity_label"] = field.value_label;
                jobLabel = field.value_label;
            }
        }
    }
    let form = $state(useForm(formInner, 'put', route('vehicles.update', { vehicle: vehicle.id })));

    function submit(e) {
        $form.processing = true;
        e.preventDefault();
        $form.put(route('vehicles.update', { vehicle: vehicle.id }), {
            onSuccess: () => {
                close();
                $form.reset();
                toast.success("Vehicle created successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error editing vehicle. " + message as string);
                }
            }
        });
    }

    let isSelectJobOpen = $state(false);

    function close() {
        show = false;
    }

</script>

{#if show}
    <Modal title="Edit Vehicle" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block}
                    <FormBlock label={block.label}>
                        {#each block.fields as field}
                            {#if field.editable}
                                {#if field.ui_type === 'related'}
                                    <RelatedSingle
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        bind:isOpen={isSelectJobOpen}
                                        bind:valLabel={jobLabel}
                                    >
                                        <JobTable
                                            view="related"
                                            onRowClick={(row) => {
                                                $form.job_id = row?.id ?? null;
                                                jobLabel = row?.title ?? '';
                                                isSelectJobOpen = false;
                                            }}
                                        />
                                    </RelatedSingle>
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
